import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { 
  AlertCircle, ChevronDown, Download, Filter, Loader, 
  Printer, Search, Settings, Book, Users, FileCheck, 
  ArrowUpDown, Eye, RefreshCw, Trash, Save, X, Check,
  BarChart, PieChart
} from 'lucide-react';

const ReportCardGenerator = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [exams, setExams] = useState([]);
  const [examConfigs, setExamConfigs] = useState([]);
  const [students, setStudents] = useState([]);
  const [reportCards, setReportCards] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [currentView, setCurrentView] = useState('setup');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [filters, setFilters] = useState({
    classId: '',
    streamId: '',
    academicYearId: '',
    termId: '',
    examIds: [],
    configId: ''
  });

  const [reportSettings, setReportSettings] = useState({
    include_previous_term: true,
    send_sms: false,
    format: 'json'
  });
  
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Data fetching
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const [classesRes, subjectsRes, yearsRes] = await Promise.all([
          api.get(`/api/v1/admin/schools/${user.school_reg}/classes`),
          api.get('/api/v1/subjects'),
          api.get('/api/v1/academic-years/list-with-terms')
        ]);
        
        setClasses(classesRes.data);
        setStreams(classesRes.data.flatMap(c => c.streams || []));
        setSubjects(subjectsRes.data);
        setAcademicYears(yearsRes.data);
        
        const currentYear = yearsRes.data.find(y => y.is_current_year);
        if (currentYear) {
          setFilters(prev => ({
            ...prev,
            academicYearId: currentYear.academic_year_id.toString(),
            termId: currentYear.terms[0]?.term_id.toString() || ''
          }));
          setTerms(currentYear.terms || []);
        }
      } catch (error) {
        console.error("Initial data error:", error);
        setErrorMessage("Failed to load initial data. Please try refreshing the page.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, []);

 // Fetch exams and term configs when term changes
useEffect(() => {
  const fetchTermData = async () => {
    if (!filters.termId || !user.school_id) return;
    
    // Maximum retry attempts
    const MAX_RETRIES = 3;
    let retryCount = 0;
    let success = false;
    
    while (retryCount < MAX_RETRIES && !success) {
      try {
        setLoading(true);
        
        // Create API URL with proper formatting
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const examsUrl = `${baseUrl}/api/v1/report-cards/exams/${filters.termId}?school_id=${user.school_id}`;
        const configsUrl = `${baseUrl}/api/v1/report-cards/configs/${filters.termId}?school_id=${user.school_id}`;

        // Use Promise.all to make parallel requests
        const [examsRes, configsRes] = await Promise.all([
          api.get(examsUrl, { timeout: 5000 }), // Add timeout
          api.get(configsUrl, { timeout: 5000 })
        ]);

        setExams(examsRes.data || []);
        setExamConfigs(configsRes.data || []);
        
        // Set default config if available
        const defaultConfig = configsRes.data?.find(c => c.is_default);
        if (defaultConfig) {
          setFilters(prev => ({ ...prev, configId: defaultConfig.id.toString() }));
        }
        
        success = true;
      } catch (error) {
        retryCount++;
        console.error(`Term data fetch error (attempt ${retryCount}/${MAX_RETRIES}):`, error);
        
        // Provide more specific error messages
        if (error.code === 'ERR_NETWORK') {
          setErrorMessage(`Network error: Could not connect to the server. Retrying... (${retryCount}/${MAX_RETRIES})`);
        } else if (error.response) {
          setErrorMessage(`Server error (${error.response.status}): ${error.response.data?.message || 'Unknown error'}`);
        } else {
          setErrorMessage(`Failed to load term data: ${error.message}. Retrying...`);
        }
        
        if (retryCount < MAX_RETRIES) {
          // Wait before retrying (increasing backoff time)
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      } finally {
        if (success || retryCount >= MAX_RETRIES) {
          setLoading(false);
          if (!success) {
            setErrorMessage("Failed to load term data after multiple attempts. Please check your connection and try again.");
          }
        }
      }
    }
  };
  
  fetchTermData();
}, [filters.termId, user.school_id]);

  // Update terms when academic year changes
  useEffect(() => {
    if (!filters.academicYearId) return;
    
    const selectedYear = academicYears.find(y => y.academic_year_id.toString() === filters.academicYearId);
    if (selectedYear) {
      setTerms(selectedYear.terms || []);
      if (selectedYear.terms && selectedYear.terms.length > 0) {
        setFilters(prev => ({ ...prev, termId: selectedYear.terms[0].term_id.toString() }));
      } else {
        setFilters(prev => ({ ...prev, termId: '' }));
      }
    }
  }, [filters.academicYearId, academicYears]);

  // Fetch students when class/stream changes
  const fetchStudents = useCallback(async () => {
    if (!filters.classId || !user.school_reg) return;
    
    try {
      setLoading(true);
      setErrorMessage('');
      const params = {
        page: 1,
        page_size: 100,
        class_id: filters.classId,
        stream_id: filters.streamId || undefined
      };
      
      const res = await api.get(`/api/v1/admin/schools/${user.school_reg}/students`, { params });
      setStudents(res.data.items || []);
      setSelectedStudentIds([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Student fetch error:", error);
      setErrorMessage("Failed to load students. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters.classId, filters.streamId, user.school_reg]);

  useEffect(() => {
    if (filters.classId) {
      fetchStudents();
    }
  }, [filters.classId, filters.streamId, fetchStudents]);

  // Generate report cards
 
 const generateReportCards = async () => {
  if (!selectedStudentIds.length) {
    setErrorMessage("Please select at least one student");
    return;
  }

  if (!filters.termId) {
    setErrorMessage("Please select a term");
    return;
  }

  setGenerating(true);
  setErrorMessage('');
  
  try {
    // Set the format to PDF as requested
    setReportSettings(prev => ({
      ...prev,
      format: 'pdf'
    }));
    
    // Determine the report card type based on selection
    let requestParams = {
      school_id: user.school_id,
      term_id: parseInt(filters.termId),
      format: 'pdf', // Set to PDF format as requested
      send_sms: reportSettings.send_sms,
      include_previous_term: reportSettings.include_previous_term
    };

    // Add configuration if selected
    if (filters.configId) {
      requestParams.config_id = parseInt(filters.configId);
    }
    
    // Add exam_id if selected
    if (filters.examId) {
      requestParams.exam_id = parseInt(filters.examId);
    }

    // Set the report card type based on selection
    if (selectedStudentIds.length === 1) {
      // If only one student, use SINGLE type with student_id
      requestParams.type = 'single';
      requestParams.student_id = selectedStudentIds[0];
    } 
    else if (filters.streamId) {
      // If stream is selected, use STREAM type
      requestParams.type = 'stream';
      requestParams.class_id = parseInt(filters.classId);
      requestParams.stream_id = parseInt(filters.streamId);
    }
    else if (filters.classId) {
      // If only class is selected, use CLASS type
      requestParams.type = 'class';
      requestParams.class_id = parseInt(filters.classId);
    }
    else {
      // All students in school (rarely used)
      requestParams.type = 'all';
    }

    // For PDF format, we need to use responseType: 'blob'
    const res = await api.get('/api/v1/report-cards/generate', { 
      params: requestParams,
      responseType: 'blob'
    });
    
    // Create a download link for the PDF file
    if (res.data) {
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // For single student, get a nicer filename if possible
      let filename = "report_cards";
      if (requestParams.type === 'single' && students) {
        const student = students.find(s => s.id === requestParams.student_id);
        if (student) {
          filename = `report_card_${student.name.replace(/\s+/g, '_')}`;
        } else {
          filename = `report_card_${requestParams.student_id}`;
        }
      } else if (requestParams.type === 'class') {
        filename = `report_cards_class_${requestParams.class_id}`;
      } else if (requestParams.type === 'stream') {
        filename = `report_cards_stream_${requestParams.stream_id}`;
      }
      
      // Create and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}_${filters.termId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success message
      setErrorMessage(""); // Clear any error message
      
      // Optionally, you can also open the PDF in a new tab
      const pdfWindow = window.open();
      pdfWindow.document.write(
        `<iframe width='100%' height='100%' src='${url}'></iframe>`
      );
      
      // Store minimal report info to show in the results view
      const tempReportCards = {
        report_cards: {}
      };
      
      // Create minimal report cards data for the results view
      selectedStudentIds.forEach(id => {
        const student = students.find(s => s.id === id);
        if (student) {
          tempReportCards.report_cards[id] = {
            student: {
              id: student.id,
              name: student.name,
              admission_number: student.admission_number,
              class: classes.find(c => c.id === student.class_id)?.name || 'Unknown'
            },
            term: {
              name: terms.find(t => t.term_id.toString() === filters.termId)?.term_name || 'Current Term'
            },
            performance: {
              // We don't have detailed performance data yet
              average: null,
              overall_grade: null
            }
          };
        }
      });
      
      setReportCards(tempReportCards);
      setCurrentView('results');
    } else {
      setErrorMessage("No report cards were generated. Please check your selection.");
    }
  } catch (error) {
    console.error("Generation error:", error);
    setErrorMessage("Failed to generate report cards: " + 
      (error.response?.data?.detail || error.response?.data?.message || error.message || "Unknown error"));
  } finally {
    setGenerating(false);
  }
};
  // View individual report
  const viewReportCard = (studentId) => {
    if (!reportCards || !reportCards.report_cards) {
      setErrorMessage("Report card data not available");
      return;
    }
    
    const card = reportCards.report_cards[studentId];
    if (!card) {
      setErrorMessage("Report not found for this student");
      return;
    }
    
    setSelectedStudent(card);
    setCurrentView('preview');
  };

  // Print report card
  const printReportCard = async (studentId) => {
    if (!studentId || !filters.termId) return;
    
    try {
      setLoading(true);
      const res = await api.get(`/api/v1/report-cards/print/${studentId}?term_id=${filters.termId}&school_id=${user.school_id}`);
      
      
      if (res.data) {
        const pdfWindow = window.open();
        pdfWindow.document.write(
          `<iframe width='100%' height='100%' src='data:application/pdf;base64,${res.data}'></iframe>`
        );
      }
    } catch (error) {
      console.error("Print error:", error);
      setErrorMessage("Failed to print report card");
    } finally {
      setLoading(false);
    }
  };

  


// Download report cards (individual or batch)
const downloadReportCard = async (studentId, format = 'pdf') => {
  if (!filters.termId || !user.school_id) {
    setErrorMessage("Term ID and School ID are required");
    return;
  }
  
  try {
    setLoading(true);
    
    // For batch export of all report cards
    if (studentId === 'batch') {
      // Get all selected student IDs or all available student IDs if in results view
      const targetStudentIds = currentView === 'results' && reportCards?.report_cards 
        ? Object.keys(reportCards.report_cards)
        : selectedStudentIds;
      
      if (!targetStudentIds || targetStudentIds.length === 0) {
        setErrorMessage("No students selected for export");
        setLoading(false);
        return;
      }

      // Prepare params for batch download
      const batchParams = {
        school_id: user.school_id,
        term_id: parseInt(filters.termId),
        format: format.toLowerCase(),
        type: 'class',
        class_id: parseInt(filters.classId)
      };
      
      // Add stream if filtered by stream
      if (filters.streamId) {
        batchParams.stream_id = parseInt(filters.streamId);
      }
      
      // Add configuration if selected
      if (filters.configId) {
        batchParams.config_id = parseInt(filters.configId);
      }
      
      // If include_previous_term is false, explicitly set it
      if (!reportSettings.include_previous_term) {
        batchParams.include_previous_term = false;
      }
      
      // Set proper response type for binary data
      const res = await api.get('/api/v1/report-cards/generate', {
        params: batchParams,
        responseType: 'blob'
      });
      
      // Create a download link for the batch file
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_cards_${filters.classId}_${filters.termId}.${format.toLowerCase()}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } 
    // For individual student download
    else {
      const res = await api.get(
        `/api/v1/report-cards/print/${studentId}/${format}`, {
          params: {
            term_id: parseInt(filters.termId),
            school_id: user.school_id,
            config_id: filters.configId ? parseInt(filters.configId) : undefined
          },
          responseType: 'blob'
        }
      );
      
      // Get student name for better filename if available
      let studentName = "student";
      if (reportCards?.report_cards && reportCards.report_cards[studentId]?.student?.name) {
        studentName = reportCards.report_cards[studentId].student.name.replace(/\s+/g, '_');
      }
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_card_${studentName}_${filters.termId}.${format.toLowerCase()}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error("Download error:", error);
    setErrorMessage("Failed to download report card: " + (error.response?.data?.detail || error.message || "Unknown error"));
  } finally {
    setLoading(false);
  }
};

// Render exams dropdown selector
const renderExamsDropdown = () => (
  <div className="mb-4">
    <label htmlFor="exam-select" className="block text-sm font-medium text-gray-700 mb-1">
      Select Exam
    </label>
    <div className="relative">
      <select
        id="exam-select"
        value={filters.examId || ''}
        onChange={(e) => setFilters(prev => ({ ...prev, examId: e.target.value }))}
        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
        disabled={loading || !exams || exams.length === 0}
      >
        <option value="">-- Select an exam --</option>
        {exams && exams.map(exam => (
          <option key={exam.id} value={exam.id}>
            {exam.name} {exam.description ? `(${exam.description})` : ''}
          </option>
        ))}
      </select>
      {loading && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}
    </div>
    {exams && exams.length === 0 && !loading && (
      <p className="mt-1 text-sm text-gray-500">No exams available for this term</p>
    )}
  </div>
);

  // Student table rendering
  const renderStudentTable = () => (
    <div className="mt-4 bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={() => {
                  const ids = students.map(s => s.id);
                  setSelectedStudentIds(selectAll ? [] : ids);
                  setSelectAll(!selectAll);
                }}
              />
            </th>
            <th>Admission #</th>
            <th>Name</th>
            <th>Class</th>
            <th>Stream</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {students.length > 0 ? (
            students.map(student => (
              <tr key={student.id}>
                <td className="px-6 py-2">
                  <input
                    type="checkbox"
                    checked={selectedStudentIds.includes(student.id)}
                    onChange={() => setSelectedStudentIds(prev => 
                      prev.includes(student.id) 
                        ? prev.filter(id => id !== student.id) 
                        : [...prev, student.id]
                    )}
                  />
                </td>
                <td className="px-6 py-2">{student.admission_number}</td>
                <td className="px-6 py-2">{student.name}</td>
                <td className="px-6 py-2">{classes.find(c => c.id === student.class_id)?.name}</td>
                <td className="px-6 py-2">{streams.find(s => s.id === student.stream_id)?.name}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="px-6 py-3 text-center text-gray-500">
                No students found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // Setup view
  const renderSetupView = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    <div className="md:col-span-2 space-y-4">
      {/* Error message display */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {errorMessage}
        </div>
      )}
      
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <Filter className="mr-2" /> Filters
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year</label>
            <select
              className="w-full border border-gray-300 rounded-md p-2"
              value={filters.academicYearId}
              onChange={e => setFilters(prev => ({
                ...prev,
                academicYearId: e.target.value
              }))}
            >
              <option value="">Select Year</option>
              {academicYears.map(year => (
                <option key={year.academic_year_id} value={year.academic_year_id}>
                  {year.year_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
            <select
              className="w-full border border-gray-300 rounded-md p-2"
              value={filters.termId}
              onChange={e => setFilters(prev => ({ 
                ...prev, 
                termId: e.target.value,
                examIds: [] // Reset exam selection when term changes
              }))}
              disabled={!filters.academicYearId || terms.length === 0}
            >
              <option value="">Select Term</option>
              {terms.map(term => (
                <option key={term.term_id} value={term.term_id}>
                  {term.term_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
            <select
              className="w-full border border-gray-300 rounded-md p-2"
              value={filters.classId}
              onChange={e => setFilters(prev => ({ 
                ...prev, 
                classId: e.target.value, 
                streamId: '' 
              }))}
            >
              <option value="">Select Class</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stream</label>
            <select
              className="w-full border border-gray-300 rounded-md p-2"
              value={filters.streamId}
              onChange={e => setFilters(prev => ({ ...prev, streamId: e.target.value }))}
              disabled={!filters.classId}
            >
              <option value="">All Streams</option>
              {streams
                .filter(s => s.class_id == filters.classId)
                .map(stream => (
                  <option key={stream.id} value={stream.id}>{stream.name}</option>
                ))}
            </select>
          </div>
          
          {examConfigs.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exam Configuration</label>
              <select
                className="w-full border border-gray-300 rounded-md p-2"
                value={filters.configId}
                onChange={e => setFilters(prev => ({ ...prev, configId: e.target.value }))}
              >
                <option value="">Default Configuration</option>
                {examConfigs.map(config => (
                  <option key={config.id} value={config.id}>
                    {config.name} {config.is_default ? "(Default)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* ADD THIS SECTION - Exam Selection */}
          <div className="md:col-span-2">
            {filters.termId && exams.length > 0 && renderExamsDropdown()}
          </div>
        </div>
      </div>

      {filters.classId && renderStudentTable()}

      <button
        onClick={generateReportCards}
        disabled={generating || !selectedStudentIds.length || !filters.termId}
        className="bg-green-700 text-white px-4 py-2 rounded disabled:opacity-50 flex items-center"
      >
        {generating ? (
          <>
            <Loader className="animate-spin mr-2 w-5 h-5" /> Generating...
          </>
        ) : (
          <>
            <FileCheck className="mr-2 w-5 h-5" /> Generate Report Cards
          </>
        )}
      </button>
    </div>

    {/* Settings Panel */}
    <div className="bg-white p-6 rounded-lg shadow space-y-4">
      <h2 className="text-lg font-semibold flex items-center">
        <Settings className="mr-2" /> Report Settings
      </h2>
      
      <div className="space-y-3">
        <label className="flex items-center justify-between">
          <span>Include Previous Term</span>
          <input
            type="checkbox"
            checked={reportSettings.include_previous_term}
            onChange={e => setReportSettings(prev => ({ 
              ...prev, 
              include_previous_term: e.target.checked 
            }))}
            className="form-checkbox h-5 w-5 text-green-600"
          />
        </label>
        
        <label className="flex items-center justify-between">
          <span>Send SMS to Parents</span>
          <input
            type="checkbox"
            checked={reportSettings.send_sms}
            onChange={e => setReportSettings(prev => ({ 
              ...prev, 
              send_sms: e.target.checked 
            }))}
            className="form-checkbox h-5 w-5 text-green-600"
          />
        </label>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
          <select
            className="w-full border border-gray-300 rounded-md p-2"
            value={reportSettings.format}
            onChange={e => setReportSettings(prev => ({ 
              ...prev, 
              format: e.target.value 
            }))}
          >
            <option value="JSON">JSON</option>
            <option value="PDF">PDF</option>
            <option value="HTML">HTML</option>
            <option value="CSV">CSV</option>
          </select>
        </div>
      </div>
    </div>
  </div>
);

  // Results view
  const renderResultsView = () => (
    <div className="space-y-4">
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {errorMessage}
        </div>
      )}
    
      <div className="flex justify-between items-center">
        <button
          onClick={() => setCurrentView('setup')}
          className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded flex items-center"
        >
          <ChevronDown className="w-4 h-4 mr-1 transform rotate-90" /> Back
        </button>
        <div className="flex gap-2">
          <button 
            className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded flex items-center"
            onClick={() => downloadReportCard('batch', 'PDF')}
          >
            <Download className="w-4 h-4 mr-1" /> Export All PDF
          </button>
          <button 
            className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded flex items-center"
            onClick={() => downloadReportCard('batch', 'CSV')}
          >
            <Download className="w-4 h-4 mr-1" /> Export Excel
          </button>
        </div>
      </div>
  
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Average</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {reportCards && reportCards.report_cards && Array.isArray(Object.values(reportCards.report_cards)) && Object.values(reportCards.report_cards).length > 0 ? (
              Object.values(reportCards.report_cards).map((card, index) => {
                // Check if card and nested properties exist
                if (!card || !card.student) {
                  console.warn("Found invalid report card data at index", index);
                  return null; // Skip rendering this row
                }
                
                return (
                  <tr key={card.student.id || index}>
                    <td className="px-6 py-4 whitespace-nowrap">{card.student?.name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{card.student?.class || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {card.performance?.average !== undefined ? `${card.performance.average.toFixed(2)}%` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{card.performance?.overall_grade || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap space-x-2">
                      <button
                        onClick={() => card.student?.id && viewReportCard(card.student.id)}
                        disabled={!card.student?.id}
                        className="text-blue-600 hover:text-blue-800 inline-flex items-center disabled:opacity-50"
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </button>
                      <button
                        onClick={() => card.student?.id && printReportCard(card.student.id)}
                        disabled={!card.student?.id}
                        className="text-gray-600 hover:text-gray-800 inline-flex items-center ml-2 disabled:opacity-50"
                      >
                        <Printer className="w-4 h-4 mr-1" /> Print
                      </button>
                      <button
                        onClick={() => card.student?.id && downloadReportCard(card.student.id, 'PDF')}
                        disabled={!card.student?.id}
                        className="text-green-600 hover:text-green-800 inline-flex items-center ml-2 disabled:opacity-50"
                      >
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </button>
                    </td>
                  </tr>
                );
              }).filter(Boolean) 
            ) : (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No report cards available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Preview view
  const renderPreview = () => (
    <div className="space-y-4">
      <button
        onClick={() => setCurrentView('results')}
        className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded flex items-center"
      >
        <ChevronDown className="w-4 h-4 mr-1 transform rotate-90" /> Back
      </button>
      
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">
            {selectedStudent.student.name}'s Report Card
          </h2>
          <div className="flex gap-2">
            <button 
              onClick={() => printReportCard(selectedStudent.student.id)}
              className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded flex items-center"
            >
              <Printer className="w-4 h-4 mr-1" /> Print
            </button>
            <button 
              onClick={() => downloadReportCard(selectedStudent.student.id, 'PDF')}
              className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded flex items-center"
            >
              <Download className="w-4 h-4 mr-1" /> PDF
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-gray-50 p-4 rounded">
          <div>
            <p><strong>Student:</strong> {selectedStudent.student.name}</p>
            <p><strong>Admission Number:</strong> {selectedStudent.student.admission_number}</p>
            <p><strong>Class:</strong> {selectedStudent.student.class}</p>
            <p><strong>Term:</strong> {selectedStudent.term.name}</p>
          </div>
          <div>
            <p><strong>Average:</strong> {selectedStudent.performance.average?.toFixed(2)}%</p>
            <p><strong>Grade:</strong> {selectedStudent.performance.overall_grade}</p>
            {selectedStudent.performance.class_position && (
              <p><strong>Position in Class:</strong> {selectedStudent.performance.class_position}</p>
            )}
            {selectedStudent.performance.stream_position && (
              <p><strong>Position in Stream:</strong> {selectedStudent.performance.stream_position}</p>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                {selectedStudent.performance.show_subject_position && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                )}
                {selectedStudent.performance.show_class_average && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class Avg</th>
                )}
                {selectedStudent.performance.track_improvement && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.values(selectedStudent.performance.subjects || {}).map(subject => (
                <tr key={subject.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{subject.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{subject.combined_score}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{subject.grade}</td>
                  {selectedStudent.performance.show_subject_position && (
                    <td className="px-6 py-4 whitespace-nowrap">{subject.position || '-'}</td>
                  )}
                  {selectedStudent.performance.show_class_average && (
                    <td className="px-6 py-4 whitespace-nowrap">{subject.class_average?.toFixed(2) || '-'}</td>
                  )}
                  {selectedStudent.performance.track_improvement && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      {subject.improvement ? (
                        <span className={subject.improvement > 0 ? "text-green-600" : "text-red-600"}>
                          {subject.improvement > 0 ? "+" : ""}{subject.improvement.toFixed(2)}%
                        </span>
                      ) : '-'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedStudent.performance.show_grade_distribution && (
          <div className="mt-6">
            <h3 className="font-semibold mb-2">Grade Distribution</h3>
            <div className="flex space-x-2">
              {Object.entries(selectedStudent.performance.grade_distribution || {}).map(([grade, count]) => (
                <div key={grade} className="bg-gray-100 px-3 py-1 rounded">
                  {grade}: {count}
                </div>
              ))}
            </div>
          </div>
        )}

     {selectedStudent.performance.teacher_comment && (
       <div className="mt-4">
         <h3 className="font-semibold mb-2">Teacher's Comment</h3>
         <p className="bg-gray-50 p-3 rounded">{selectedStudent.performance.teacher_comment}</p>
       </div>
     )}

     {selectedStudent.performance.principal_comment && (
       <div className="mt-4">
         <h3 className="font-semibold mb-2">Principal's Comment</h3>
         <p className="bg-gray-50 p-3 rounded">{selectedStudent.performance.principal_comment}</p>
       </div>
     )}
     
     {selectedStudent.performance.charts && (
       <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
         <div className="bg-gray-50 p-4 rounded">
           <h3 className="font-semibold mb-2 flex items-center">
             <BarChart className="w-4 h-4 mr-1" /> Performance Trends
           </h3>
           <div className="h-64">
             {/* Chart would be rendered here */}
           </div>
         </div>
         <div className="bg-gray-50 p-4 rounded">
           <h3 className="font-semibold mb-2 flex items-center">
             <PieChart className="w-4 h-4 mr-1" /> Subject Distribution
           </h3>
           <div className="h-64">
             {/* Chart would be rendered here */}
           </div>
         </div>
       </div>
     )}
   </div>
  </div>
);

// Render the main component
return (
 <div className="p-4">
   <header className="mb-6">
     <h1 className="text-2xl font-bold flex items-center">
       <Book className="mr-2" /> Report Card Generator
     </h1>
     <p className="text-gray-600">Generate and manage student report cards</p>
   </header>

   {/* Loading overlay */}
   {loading && (
     <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
       <div className="bg-white p-4 rounded-lg shadow-lg flex items-center">
         <Loader className="animate-spin mr-2" /> Loading...
       </div>
     </div>
   )}

   {/* Main content */}
   {currentView === 'setup' && renderSetupView()}
   {currentView === 'results' && renderResultsView()}
   {currentView === 'preview' && renderPreview()}
 </div>
);
};

export default ReportCardGenerator;